import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useParties } from "@/features/kyc/api/use-parties";
import type { PartyFilter } from "@/features/kyc/api/dto";
import {
  PartyFormDialog,
  partiesColumns,
} from "@/features/kyc/components";

/** Traduit les filtres de colonnes DataTableClar en `PartyFilter` (§4.2). */
const toPartyFilter = (filters: ColumnFiltersState): PartyFilter => {
  const filter: PartyFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "partyType") filter.partyType = value as PartyFilter["partyType"];
    if (id === "kycStatus") filter.kycStatus = value as PartyFilter["kycStatus"];
  }
  return filter;
};

/** Liste paginée des identités — file de travail back-office (POST /Parties/all). */
export function PartiesPage() {
  const { t } = useTypedTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toPartyFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useParties(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("kyc.parties.title")}
        </h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("kyc.parties.create")}
        </Button>
      </div>

      <DataTableClar
        data={data?.items ?? []}
        columnsClar={partiesColumns}
        title={t("kyc.parties.title")}
        exportFileName="parties"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <PartyFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
